/* ============================================================================
   TPW — THE COMPLETION ENGINE (engine) · v-now: ONE CARD
   ----------------------------------------------------------------------------
   Direction C "Concierge Ritual". Founder ruling 2026-07-05: on the FIRST
   qualifying add-to-cart of the session, show ONE card — the single best
   recommendation — with Add to cart and Skip/close. No Reel, no series, no
   "next recommendation" (all parked for v4).

   HOW IT HOOKS (additive, Pythia P0):
     Listens for the theme's own "variant:add" DOM event, which product-form.js
     dispatches AFTER a SUCCESSFUL /cart/add.js on EVERY surface (PDP, quick-add
     card). We fire strictly on success, never intercept /cart/add, never touch
     cart.js, and never swallow the native add_to_cart analytics. On trigger we
     do ONE /cart.js read, then ONE /products/{handle}.js read to hydrate the
     added line's product tags (Shopify cart line items carry NO tags — the
     root cause of the CE-03 defect class), resolve the engine-category in
     memory from the JSON blob, apply suppression + bundle escalation, pick the
     highest-priority in-stock suggestion, lazily inject + populate the panel,
     and run the reveal. ONCE PER SESSION (sessionStorage, survives reloads).

   ONE-TAP ADD uses /cart/add.js then lets the theme's own section refresh path
   update the cart icon + drawer (we mark __tpwCartHandled and fire cart:change/
   cartUpdate the way the theme does, then call refreshCartSections).

   PERF: panel DOM is lazy-injected on first trigger. Data is static in-memory.
   Fonts are the theme's self-hosted Outfit + DM Mono (no Google Fonts).
   ============================================================================ */
(function () {
  "use strict";
  if (window.__tpwCE) return;
  window.__tpwCE = true;

  /* ---- load static data ---- */
  var DATA = null;
  try {
    var el = document.getElementById("tpw-ce-data");
    if (el) DATA = JSON.parse(el.textContent);
  } catch (e) {
    /* malformed data → engine stays a silent no-op */
  }
  if (!DATA || !DATA.rules || !DATA.products) return;

  var CFG = DATA.config || {};
  var THRESHOLD_CENTS = (CFG.freeShipThreshold || 150) * 100;
  var LOW_STOCK = CFG.lowStockThreshold || 5;
  var ADVANCE_MIN_CENTS = ((CFG.shipBar && CFG.shipBar.advanceMin) || 30) * 100;
  var BUNDLE_SATISFIES = CFG.bundleSatisfiesCategories || [];
  var EXCLUDE_TAGS = (CFG.excludeTags || []).map(function (t) { return t.toLowerCase(); });
  var EXCLUDE_HANDLES = (CFG.excludeHandles || []).map(function (h) { return String(h).toLowerCase(); });

  var reduceMotion = window.matchMedia("(prefers-reduced-motion:reduce)").matches;

  /* ---- helpers ---- */
  function lc(s) { return (s == null ? "" : String(s)).toLowerCase(); }
  function lcArr(a) { return (a || []).map(lc); }
  function anyIn(needles, haystack) {
    for (var i = 0; i < needles.length; i++) if (haystack.indexOf(needles[i]) > -1) return true;
    return false;
  }
  function anyContains(needles, str) {
    for (var i = 0; i < needles.length; i++) if (str.indexOf(needles[i]) > -1) return true;
    return false;
  }
  function money(cents) {
    var n = (cents || 0) / 100;
    return "$" + (Number.isInteger(n) ? n : n.toFixed(2));
  }
  function money2(cents) { return "$" + ((cents || 0) / 100).toFixed(2); }
  function pct(cents) { return Math.max(0, Math.min(100, (cents / THRESHOLD_CENTS) * 100)); }

  /* ============================================================
     CATEGORY RESOLUTION — mirror the resolver from the JSON.
     Input: a Shopify cart line item (or product-ish object) with
     product_type, product_title/title, handle, variant title, tags.
     ============================================================ */
  function resolveCategory(item) {
    var ptype = lc(item.product_type);
    var tags = lcArr(item.tags || item.product_tags || []);
    var title = lc(item.product_title || item.title);
    var vtitle = lc(item.variant_title || item.variantTitle);
    var handle = lc(item.handle || item.product_handle);

    // hard exclude — by tag (works once tags are hydrated) AND by handle (the
    // kill-switch that needs no admin tagging; cart lines carry no tags)
    if (anyIn(EXCLUDE_TAGS, tags)) return null;
    if (handle && EXCLUDE_HANDLES.indexOf(handle) > -1) return null;

    var rules = (DATA.categoryResolver && DATA.categoryResolver.rules) || [];
    // rules array is written in priority order (highest first)
    for (var i = 0; i < rules.length; i++) {
      var r = rules[i];
      if (r.productTypeAny && r.productTypeAny.map(lc).indexOf(ptype) === -1) continue;
      if (r.handleAny && r.handleAny.map(lc).indexOf(handle) === -1) continue;
      if (r.tagNotAny && anyIn(lcArr(r.tagNotAny), tags)) continue;

      // Tag / title refinement: when a rule lists tagAny/titleContainsAny it is a
      // POSITIVE refinement — match if EITHER hits (some catalog items are typed
      // right but under-tagged, so a productType-only match still counts unless
      // the rule is purely tag-gated). We treat productTypeAny as sufficient and
      // tagAny/titleContainsAny as bonus disambiguators, except EHMD/HEAT_SCREEN
      // which REQUIRE their tag/title signal to split from HMD_DEVICE.
      var needsTagGate = r.category === "EHMD" || r.category === "HEAT_SCREEN";
      if (needsTagGate) {
        var tagHit = r.tagAny && anyIn(lcArr(r.tagAny), tags);
        var titleHit = r.titleContainsAny && anyContains(r.titleContainsAny.map(lc), title);
        if (!tagHit && !titleHit) continue;
      }
      // FOIL_POKED requires the poked signal on variant/title/tag
      if (r.category === "FOIL_POKED") {
        var vHit = r.variantTitleAny && anyContains(r.variantTitleAny.map(lc), vtitle);
        var tHit = r.tagAny && anyIn(lcArr(r.tagAny), tags);
        if (!vHit && !tHit) continue;
      }
      // COAL_QUICKLIGHT: productTypeAny ("Charcoal - Quick Light") IS the gate.
      // The old hard tag requirement made quicklights invisible at runtime
      // (cart lines carry no tags, and the catalog tag is "Quick-Light
      // Charcoal" which never exact-matched anyway) — CE-03b.
      return r.category;
    }
    return null;
  }

  /* Build the SET of categories currently covered by the cart (for suppression). */
  function cartCategories(cartItems) {
    var set = {};
    for (var i = 0; i < cartItems.length; i++) {
      var cat = resolveCategory(cartItems[i]);
      if (cat) {
        set[cat] = true;
        if (cat === "BUNDLE_PREPKIT") {
          for (var j = 0; j < BUNDLE_SATISFIES.length; j++) set[BUNDLE_SATISFIES[j]] = true;
        }
      }
    }
    return set;
  }

  /* ============================================================
     PICK — given the added item's category + the cart, choose the
     winning rule + resolved live product (skip out-of-stock / dark).
     ============================================================ */
  function liveProduct(handle) {
    var p = DATA.products[handle];
    if (!p || !p.variantId || p.available !== true) return null;
    return p;
  }

  function resolveSuggestionProduct(rule) {
    // prefer suggestion, else fallback; require live + in-stock
    return liveProduct(rule.suggest) || liveProduct(rule.fallback) || null;
  }

  function shouldEscalateBundle(addedCat, catSet) {
    var esc = DATA.bundleEscalation;
    if (!esc) return false;
    if (catSet["BUNDLE_PREPKIT"]) return false;
    var hasAnchor = false;
    for (var i = 0; i < esc.cartHasAnyCategory.length; i++) {
      if (catSet[esc.cartHasAnyCategory[i]] || addedCat === esc.cartHasAnyCategory[i]) { hasAnchor = true; break; }
    }
    if (!hasAnchor) return false;
    var missing = 0;
    for (var k = 0; k < esc.missingFrom.length; k++) {
      if (!catSet[esc.missingFrom[k]]) missing++;
    }
    return missing >= (esc.cartMissingAtLeastN || 2);
  }

  function pick(addedCat, addedItem, cartItems) {
    if (!addedCat) return null;
    var catSet = cartCategories(cartItems);

    // gather candidate rules whose trigger matches the added category,
    // sorted by priority (desc), applying suppression.
    var candidates = [];
    for (var i = 0; i < DATA.rules.length; i++) {
      var r = DATA.rules[i];
      if (r.trigger.indexOf(addedCat) === -1) continue;
      // trigger tag refinement (e.g. R10 requires HMD-compatible bowl)
      if (r.requireTriggerTagAny) {
        var atags = lcArr(addedItem.tags || []);
        if (!anyIn(r.requireTriggerTagAny.map(lc), atags)) continue;
      }
      // pipes that SHIP WITH a bowl: skip the bowl suggestion (routes to next gap)
      if (r.suppressForHandleAny) {
        var ceh = lc(addedItem.handle || addedItem.product_handle || "");
        if (r.suppressForHandleAny.map(lc).indexOf(ceh) !== -1) continue;
      }
      // suppression: skip if the suggested category is already covered
      var suppressed = false;
      var sup = r.suppressIfCartHas || [];
      for (var s = 0; s < sup.length; s++) { if (catSet[sup[s]]) { suppressed = true; break; } }
      if (suppressed) continue;
      var prod = resolveSuggestionProduct(r);
      if (!prod) continue; // nothing in stock for this rule → skip
      // never offer the very product that was just added (CE-06 class)
      if (lc(addedItem.handle || addedItem.product_handle) === lc(prod.handle)) continue;
      candidates.push({ rule: r, product: prod });
    }
    if (!candidates.length) return null;
    candidates.sort(function (a, b) { return (b.rule.priority || 0) - (a.rule.priority || 0); });
    var top = candidates[0];

    // bundle escalation: if building from scratch + missing >=2 essentials,
    // promote the Prep Kit bundle over the single item (only if the winning rule
    // allows escalation and the bundle itself is live/in-stock).
    if (top.rule.bundleEscalate && shouldEscalateBundle(addedCat, catSet)) {
      var esc = DATA.bundleEscalation;
      var bundleProd = liveProduct(esc.handle);
      if (bundleProd) {
        return {
          rule: {
            id: top.rule.id + "+ESC",
            // the reason must name what was ACTUALLY added (CE-02): per-anchor
            // variant when we have one, else the anchor-neutral base line
            reason: (esc.reasonByAnchor && esc.reasonByAnchor[addedCat]) || esc.reason,
            eyebrow: esc.eyebrow,
            addLabel: esc.addLabel,
            shipMode: top.rule.shipMode,
            social: null,
            isBundle: true
          },
          product: bundleProd,
          downsell: { rule: top.rule, product: top.product },
          catSet: catSet
        };
      }
    }

    return { rule: top.rule, product: top.product, downsell: null, catSet: catSet };
  }

  /* ============================================================
     PANEL DOM — lazy-injected once, on first trigger.
     ============================================================ */
  var els = null;
  function svgPlus() { return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>'; }
  function svgCheck() { return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>'; }

  function buildPanel() {
    if (els) return els;
    var root = document.createElement("div");
    root.className = "tpwce-root";
    root.innerHTML =
      '<div class="tpwce-backdrop" data-tpwce-close></div>' +
      '<aside class="tpwce-panel" role="dialog" aria-modal="true" aria-labelledby="tpwceHeroName" aria-describedby="tpwceReason" hidden>' +
        '<div class="tpwce-head">' +
          '<div class="tpwce-confirm">' +
            '<span class="tpwce-check" aria-hidden="true">' + svgCheck() + '</span>' +
            '<span class="tpwce-cname" data-tpwce="addedName"></span>' +
          '</div>' +
          '<button class="tpwce-close" data-tpwce-close aria-label="Close and keep shopping">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="tpwce-body" data-tpwce="body">' +
          '<div class="tpwce-ritual">' +
            '<div class="tpwce-stage" data-tpwce="stage">' +
              '<img class="tpwce-hero-img" data-tpwce="heroImg" alt="" aria-hidden="true">' +
              '<div class="tpwce-seal" aria-hidden="true"></div>' +
              '<div class="tpwce-stamp" aria-hidden="true">' + svgCheck() + ' Free shipping unlocked</div>' +
            '</div>' +
            '<div class="tpwce-narr">' +
              '<p class="tpwce-eyebrow" data-tpwce="eyebrow"></p>' +
              '<p class="tpwce-reason" id="tpwceReason" data-tpwce="reason"></p>' +
              '<h2 class="tpwce-hero-name" id="tpwceHeroName"><a href="#" data-tpwce="heroLink"></a></h2>' +
              '<div class="tpwce-meta-row" data-tpwce="metaRow">' +
                '<span class="tpwce-price" data-tpwce="price"></span>' +
                '<span class="tpwce-save" data-tpwce="save" hidden></span>' +
                '<span class="tpwce-stock" data-tpwce="stock" hidden><span class="tpwce-dot"></span><span data-tpwce="stockTxt"></span></span>' +
              '</div>' +
              '<p class="tpwce-social" data-tpwce="social" hidden>' +
                '<span class="tpwce-social-glyph" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><polyline points="16 11 18.5 13.5 23 8.5"/></svg></span>' +
                '<span class="tpwce-social-txt" data-tpwce="socialTxt"></span>' +
              '</p>' +
              '<div class="tpwce-dispatch" data-tpwce="dispatch" hidden>' +
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15.5 14"/></svg>' +
                '<span>On your doorstep today if you order in <span class="tpwce-clock" data-tpwce="clock"></span></span>' +
              '</div>' +
            '</div>' +
          '</div>' +
          '<div class="tpwce-fade" data-tpwce="fade" aria-hidden="true"></div>' +
        '</div>' +
        '<div class="tpwce-action">' +
          '<button class="tpwce-add" data-tpwce="add">' + svgPlus() + '<span data-tpwce="addLabel"></span></button>' +
          '<p class="tpwce-err" data-tpwce="err" role="alert" hidden>Sorry, that could not be added. Please try again.</p>' +
          '<div class="tpwce-downsell" data-tpwce="downsell" hidden>' +
            '<div class="tpwce-downsell-l">' +
              '<div class="tpwce-downsell-lbl" data-tpwce="downsellLbl"></div>' +
              '<div class="tpwce-downsell-name" data-tpwce="downsellName"></div>' +
            '</div>' +
            '<button class="tpwce-downsell-add" data-tpwce="downsellAdd">Add</button>' +
          '</div>' +
          '<button class="tpwce-skip" data-tpwce-close type="button">Skip for now</button>' +
        '</div>' +
        '<div class="tpwce-ship" data-tpwce="ship">' +
          '<div class="tpwce-ship-top">' +
            '<div class="tpwce-ship-msg" data-tpwce="shipMsg"></div>' +
            '<div class="tpwce-ship-fig" data-tpwce="shipFig"></div>' +
          '</div>' +
          '<div class="tpwce-track" data-tpwce="track">' +
            '<div class="tpwce-track-fill" data-tpwce="trackFill"></div>' +
            '<div class="tpwce-track-ghost" data-tpwce="trackGhost"></div>' +
          '</div>' +
          '<div class="tpwce-cross" data-tpwce="cross">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>' +
            '<span>Add this and shipping is on us</span>' +
          '</div>' +
          '<div class="tpwce-unlock-flash">' +
            '<span class="tpwce-medal" aria-hidden="true">' + svgCheck() + '</span>' +
            '<span>Free shipping unlocked</span>' +
          '</div>' +
        '</div>' +
        '<div class="tpwce-sr-only" data-tpwce="livePolite" role="status" aria-live="polite"></div>' +
        '<div class="tpwce-sr-only" data-tpwce="liveAssertive" role="status" aria-live="assertive"></div>' +
      '</aside>';
    document.body.appendChild(root);

    var q = function (name) { return root.querySelector('[data-tpwce="' + name + '"]'); };
    els = {
      root: root,
      panel: root.querySelector(".tpwce-panel"),
      backdrop: root.querySelector(".tpwce-backdrop"),
      addedName: q("addedName"),
      stage: q("stage"), heroImg: q("heroImg"),
      eyebrow: q("eyebrow"), reason: q("reason"),
      heroLink: q("heroLink"), metaRow: q("metaRow"),
      price: q("price"), save: q("save"),
      stock: q("stock"), stockTxt: q("stockTxt"),
      social: q("social"), socialTxt: q("socialTxt"),
      dispatch: q("dispatch"), clock: q("clock"),
      add: q("add"), addLabel: q("addLabel"), err: q("err"),
      downsell: q("downsell"), downsellLbl: q("downsellLbl"),
      downsellName: q("downsellName"), downsellAdd: q("downsellAdd"),
      ship: q("ship"), shipMsg: q("shipMsg"), shipFig: q("shipFig"),
      track: q("track"), trackFill: q("trackFill"), trackGhost: q("trackGhost"),
      cross: q("cross"), body: q("body"), fade: q("fade"),
      livePolite: q("livePolite"), liveAssertive: q("liveAssertive")
    };

    // events
    root.querySelectorAll("[data-tpwce-close]").forEach(function (b) {
      b.addEventListener("click", closePanel);
    });
    els.add.addEventListener("click", onPrimaryAdd);
    els.downsellAdd.addEventListener("click", onDownsellAdd);
    els.heroLink.addEventListener("click", function (e) {
      // real crawlable link; let normal navigation happen (closes via unload)
      if (els.heroLink.getAttribute("href") === "#") e.preventDefault();
    });
    els.body.addEventListener("scroll", updateFade);
    window.addEventListener("resize", updateFade);
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && els.root.classList.contains("tpwce-open")) closePanel();
    });
    // light focus trap
    els.panel.addEventListener("keydown", function (e) {
      if (e.key !== "Tab") return;
      var f = els.panel.querySelectorAll("button:not([disabled]),a[href]");
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    return els;
  }

  /* ============================================================
     STATE + RENDER
     ============================================================ */
  var state = null;      // current open panel state
  var ritualTimers = [];
  var clockTimer = null;
  var lastFocus = null;

  function clearTimers() { ritualTimers.forEach(clearTimeout); ritualTimers = []; }
  function schedule(fn, ms) { var t = setTimeout(fn, ms); ritualTimers.push(t); return t; }

  function resetRitual() {
    clearTimers();
    els.stage.classList.remove("tpwce-img-in", "tpwce-sealed");
    [els.eyebrow, els.reason, els.heroName, els.metaRow, els.social, els.dispatch].forEach(function (el) {
      if (el) el.classList.remove("tpwce-in");
    });
    els.heroName = els.heroName || document.getElementById("tpwceHeroName");
    if (els.heroName) els.heroName.classList.remove("tpwce-in");
    els.eyebrow.innerHTML = "";
  }

  function typeEyebrow(text, done) {
    if (reduceMotion) { els.eyebrow.textContent = text; els.eyebrow.classList.add("tpwce-in"); done && done(); return; }
    els.eyebrow.classList.add("tpwce-in");
    var i = 0;
    els.eyebrow.innerHTML = '<span class="tpwce-ct"></span><span class="tpwce-cursor">|</span>';
    var ct = els.eyebrow.querySelector(".tpwce-ct");
    var step = function () {
      ct.textContent = text.slice(0, i);
      i++;
      if (i <= text.length) schedule(step, 34);
      else schedule(function () { els.eyebrow.textContent = text; done && done(); }, 240);
    };
    step();
  }

  function playRitual() {
    var heroName = document.getElementById("tpwceHeroName");
    if (reduceMotion) {
      els.stage.classList.add("tpwce-img-in");
      els.eyebrow.textContent = state.rule.eyebrow || "Your setup";
      els.eyebrow.classList.add("tpwce-in");
      [els.reason, heroName, els.metaRow, els.social, els.dispatch].forEach(function (el) { if (el) el.classList.add("tpwce-in"); });
      updateFade();
      return;
    }
    typeEyebrow(state.rule.eyebrow || "Your setup", function () {});
    schedule(function () { els.stage.classList.add("tpwce-img-in"); }, 260);
    schedule(function () { els.reason.classList.add("tpwce-in"); }, 540);
    schedule(function () { if (heroName) heroName.classList.add("tpwce-in"); }, 780);
    schedule(function () { els.metaRow.classList.add("tpwce-in"); }, 1010);
    schedule(function () { if (!els.social.hidden) els.social.classList.add("tpwce-in"); }, 1230);
    schedule(function () { if (!els.dispatch.hidden) els.dispatch.classList.add("tpwce-in"); }, 1430);
    schedule(updateFade, 1550);
  }

  function startClock() {
    stopClock();
    var eta = (window.TPWStoreHours && window.TPWStoreHours.dispatchETA)
      ? window.TPWStoreHours.dispatchETA(new Date())
      : null;
    if (!eta || !eta.sameDay || !eta.secondsToCutoff) { els.dispatch.hidden = true; return; }
    els.dispatch.hidden = false;
    var secs = eta.secondsToCutoff;
    var render = function () {
      if (secs <= 0) { els.dispatch.hidden = true; stopClock(); return; }
      var h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), sec = secs % 60;
      els.clock.textContent = h > 0 ? (h + "h " + String(m).padStart(2, "0") + "m")
                                    : (m + "m " + String(sec).padStart(2, "0") + "s");
    };
    render();
    clockTimer = setInterval(function () { secs--; render(); }, 1000);
  }
  function stopClock() { if (clockTimer) { clearInterval(clockTimer); clockTimer = null; } }

  /* Decide the ship mode for this pick given real cart total + suggestion price. */
  function computeShipMode(cartTotalCents, suggestPriceCents, ruleShipMode) {
    if (ruleShipMode === "suppress") return "suppress";
    var projected = cartTotalCents + suggestPriceCents;
    if (cartTotalCents < THRESHOLD_CENTS && projected >= THRESHOLD_CENTS) return "cross";
    var gap = THRESHOLD_CENTS - cartTotalCents;
    if (gap <= 0) return "suppress"; // already free — nothing to say
    if (suggestPriceCents >= ADVANCE_MIN_CENTS) return "advance";
    return "suppress"; // too small to matter → completion stands alone
  }

  function renderShipPreAdd() {
    var s = state;
    if (s.shipMode === "suppress") {
      els.ship.hidden = true;
      // ship bar gone → the action zone is now the sheet's bottom-most element,
      // so its last child (the Skip button) must clear the home indicator /
      // preview bar. Flag it for the CSS (class-based, no :has() dependency).
      els.panel.classList.add("tpwce-ship-off");
      return;
    }
    els.ship.hidden = false;
    els.panel.classList.remove("tpwce-ship-off");
    var gap = THRESHOLD_CENTS - s.cartTotal;
    els.trackFill.style.width = pct(s.cartTotal) + "%";
    els.shipFig.textContent = money(Math.min(s.cartTotal, THRESHOLD_CENTS)) + " / " + money(THRESHOLD_CENTS);
    els.shipMsg.innerHTML = "You're <span class=\"tpwce-amt\">" + money2(gap) + "</span> from free shipping";
    var projected = s.cartTotal + s.suggestPrice;
    var willCross = s.cartTotal < THRESHOLD_CENTS && projected >= THRESHOLD_CENTS;
    if (willCross) {
      els.track.classList.add("tpwce-show-ghost");
      els.trackGhost.style.left = pct(s.cartTotal) + "%";
      els.trackGhost.style.width = (pct(projected) - pct(s.cartTotal)) + "%";
      els.cross.classList.add("tpwce-on");
    } else {
      els.track.classList.remove("tpwce-show-ghost");
      els.trackGhost.style.width = "0%";
      els.cross.classList.remove("tpwce-on");
    }
  }

  function renderShipPostAdd() {
    var s = state;
    if (s.shipMode === "suppress") return;
    els.track.classList.remove("tpwce-show-ghost");
    els.cross.classList.remove("tpwce-on");
    if (s.cartTotal >= THRESHOLD_CENTS) {
      els.track.classList.add("tpwce-unlocked");
      els.trackFill.style.width = "100%";
      els.ship.classList.add("tpwce-is-unlocked");
      els.shipMsg.innerHTML = 'Free shipping <span class="tpwce-amt">unlocked</span>';
      els.shipFig.textContent = money(THRESHOLD_CENTS) + " / " + money(THRESHOLD_CENTS);
      els.liveAssertive.textContent = "Free shipping unlocked.";
      els.stage.classList.add("tpwce-sealed"); // THE ONE GOLD MOMENT
    } else {
      els.trackFill.style.width = pct(s.cartTotal) + "%";
      var gap = THRESHOLD_CENTS - s.cartTotal;
      els.shipMsg.innerHTML = "You're <span class=\"tpwce-amt\">" + money2(gap) + "</span> from free shipping";
      els.shipFig.textContent = money(s.cartTotal) + " / " + money(THRESHOLD_CENTS);
    }
  }

  function updateFade() {
    if (!els) return;
    requestAnimationFrame(function () {
      var overflow = els.body.scrollHeight - els.body.clientHeight;
      var atBottom = els.body.scrollTop >= (overflow - 2);
      els.fade.classList.toggle("tpwce-on", overflow > 4 && !atBottom);
    });
  }

  /* Render the panel for a fresh pick + open it. */
  function openWith(picked, addedItem, cartTotalCents) {
    buildPanel();
    var rule = picked.rule, product = picked.product;

    state = {
      rule: rule,
      product: product,
      downsell: picked.downsell,
      cartTotal: cartTotalCents,
      suggestPrice: product.price,
      added: false,
      shipMode: computeShipMode(cartTotalCents, product.price, rule.shipMode)
    };

    // confirmation strip
    els.addedName.textContent = (addedItem.product_title || addedItem.title || "Item") + " added";

    resetRitual();

    // hero image — FOUNDER CLARITY FIX handled in CSS (object-fit:contain + pad)
    els.heroImg.src = product.image || "";
    els.heroImg.alt = product.imageAlt || product.title || "";

    els.reason.innerHTML = rule.reason || "Here's what finishes it.";
    els.heroLink.textContent = product.title;
    els.heroLink.setAttribute("href", product.url || "#");
    els.price.textContent = money(product.price);

    // save chip (bundle only) — computed from the LIVE compare-at price, never
    // a static approximation (CE-20: no "~" in customer copy)
    var saveCents = (product.compareAtPrice || 0) - product.price;
    if (rule.isBundle && saveCents > 0) { els.save.textContent = "saves " + money(saveCents); els.save.hidden = false; }
    else els.save.hidden = true;

    // RED low-stock tag — only when inventory is genuinely low + tracked
    var showStock = product.inventoryManaged === "shopify" &&
                    product.inventoryPolicy !== "continue" &&
                    typeof product.inventoryQuantity === "number" &&
                    product.inventoryQuantity > 0 &&
                    product.inventoryQuantity <= LOW_STOCK;
    if (showStock) { els.stockTxt.textContent = "Only " + product.inventoryQuantity + " left"; els.stock.hidden = false; }
    else els.stock.hidden = true;

    // social proof
    if (rule.social) { els.socialTxt.innerHTML = rule.social; els.social.hidden = false; }
    else els.social.hidden = true;

    // add button reset
    els.add.classList.remove("tpwce-is-added", "tpwce-is-busy");
    els.add.disabled = false;
    els.addLabel.textContent = rule.addLabel || "Add to cart";
    var addSvg = els.add.querySelector("svg"); if (addSvg) addSvg.outerHTML = svgPlus();
    els.err.hidden = true;

    // downsell (bundle escalation demotes single item to secondary)
    if (picked.downsell) {
      var d = picked.downsell;
      els.downsell.hidden = false;
      els.downsellLbl.textContent = "Or just the essential";
      els.downsellName.textContent = d.product.title + " ";
      var dPrice = document.createElement("span");
      dPrice.className = "tpwce-p";
      dPrice.textContent = money(d.product.price);
      els.downsellName.appendChild(dPrice);
      els.downsellAdd.disabled = false;
      els.downsellAdd.textContent = "Add";
    } else {
      els.downsell.hidden = true;
    }

    // ship bar reset
    els.ship.classList.remove("tpwce-is-unlocked");
    els.track.classList.remove("tpwce-unlocked", "tpwce-show-ghost");
    els.cross.classList.remove("tpwce-on");
    els.trackGhost.style.width = "0%";
    els.livePolite.textContent = "";
    els.liveAssertive.textContent = "";

    if (els.body) els.body.hidden = false;
    els.ship.hidden = false;

    els.panel.hidden = false;
    document.documentElement.classList.add("tpwce-lock");
    lastFocus = document.activeElement;
    els.livePolite.textContent = (addedItem.product_title || addedItem.title || "Item") + " added to cart";

    if (state.shipMode !== "suppress") els.trackFill.style.width = "0%";
    // force reflow then slide in
    void els.panel.offsetWidth;
    els.root.classList.add("tpwce-open");

    schedule(function () { playRitual(); }, reduceMotion ? 0 : 120);
    startClock();

    renderShipPreAdd();
    requestAnimationFrame(function () { renderShipPreAdd(); updateFade(); });
    setTimeout(function () {
      renderShipPreAdd(); updateFade();
      var closeBtn = els.panel.querySelector(".tpwce-close");
      if (closeBtn) closeBtn.focus();
    }, 360);

    markSessionFired();
    analytics("ce_shown", { rule: rule.id, suggestion: product.handle });
  }

  function closePanel() {
    if (!els) return;
    els.root.classList.remove("tpwce-open");
    document.documentElement.classList.remove("tpwce-lock");
    clearTimers();
    stopClock();
    setTimeout(function () { els.panel.hidden = true; }, 340);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ============================================================
     ONE-TAP ADD — /cart/add.js, then hand off to the theme's own
     cart refresh (mark handled + fire the theme's cart events +
     refresh sections). Never blocks; never double-opens the drawer.
     ============================================================ */
  function addToCart(variantId, onDone) {
    var body = { items: [{ id: variantId, quantity: 1 }] };
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function () { return fetch("/cart.js").then(function (r) { return r.json(); }); })
      .then(function (cart) {
        // mark this add as theme-handled so cart.js's global interceptor doesn't
        // double-fetch, then refresh the icon + drawer via the theme's helper.
        window.__tpwCartHandled = Date.now();
        try {
          document.dispatchEvent(new CustomEvent("cart:change", { detail: { cart: cart, sectionId: "tpw-completion-engine" } }));
        } catch (e) {}
        try {
          if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: "tpw-completion-engine" });
          }
        } catch (e) {}
        refreshCartSections();
        onDone && onDone(true, cart);
      })
      .catch(function () { onDone && onDone(false); });
  }

  // mirror cart.js's external-add section refresh (icon bubble + drawer), no open.
  function refreshCartSections() {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
    fetch(root + "?sections=cart-icon-bubble,cart-drawer", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        if (sections["cart-icon-bubble"]) {
          var h1 = document.createElement("div");
          h1.innerHTML = sections["cart-icon-bubble"];
          var freshBubble = h1.querySelector(".shopify-section") || h1.firstElementChild;
          var liveBubble = document.getElementById("cart-icon-bubble");
          if (freshBubble && liveBubble) liveBubble.innerHTML = freshBubble.innerHTML;
        }
        if (sections["cart-drawer"]) {
          var h2 = document.createElement("div");
          h2.innerHTML = sections["cart-drawer"];
          var freshDrawer = h2.querySelector("#CartDrawer");
          var cartDrawer = document.querySelector("cart-drawer");
          var liveDrawer = cartDrawer && cartDrawer.querySelector("#CartDrawer");
          if (freshDrawer && liveDrawer) {
            liveDrawer.innerHTML = freshDrawer.innerHTML;
            cartDrawer.classList.remove("is-empty");
            var inner = cartDrawer.querySelector(".cart-drawer__inner");
            var cdi = cartDrawer.querySelector("cart-drawer-items");
            if (inner) inner.classList.remove("is-empty");
            if (cdi) cdi.classList.remove("is-empty");
            var overlay = cartDrawer.querySelector("#CartDrawer-Overlay");
            if (overlay) overlay.addEventListener("click", function () { cartDrawer.close(); });
          }
        }
      })
      .catch(function () {});
  }

  function onPrimaryAdd() {
    if (!state || state.added) return;
    var product = state.product;
    els.err.hidden = true;
    els.add.classList.add("tpwce-is-busy");
    els.add.disabled = true;
    addToCart(product.variantId, function (ok) {
      els.add.classList.remove("tpwce-is-busy");
      if (!ok) {
        // VISIBLE failure state (CE-09), not sr-only alone
        els.add.disabled = false;
        els.err.hidden = false;
        els.liveAssertive.textContent = "Sorry, that could not be added. Please try again.";
        return;
      }
      state.added = true;
      state.cartTotal += product.price;
      els.add.classList.add("tpwce-is-added");
      els.addLabel.textContent = "Added to cart";
      var s = els.add.querySelector("svg"); if (s) s.outerHTML = svgCheck();
      els.livePolite.textContent = product.title + " added to cart";
      if (!els.downsell.hidden) els.downsellAdd.disabled = true;
      analytics("ce_accepted", { rule: state.rule.id, suggestion: product.handle });
      // Founder: once added, close the whole sheet so they can move on.
      // Brief confirmation beat (checkmark + "Added to cart"), then auto-close.
      setTimeout(function () { closePanel(); }, 950);
    });
  }

  function onDownsellAdd() {
    if (!state || state.added || !state.downsell) return;
    var d = state.downsell.product;
    els.err.hidden = true;
    els.downsellAdd.disabled = true;
    els.downsellAdd.textContent = "…";
    addToCart(d.variantId, function (ok) {
      if (!ok) {
        // VISIBLE failure state (CE-09)
        els.downsellAdd.disabled = false;
        els.downsellAdd.textContent = "Add";
        els.err.hidden = false;
        els.liveAssertive.textContent = "Sorry, that could not be added. Please try again.";
        return;
      }
      state.added = true;
      state.cartTotal += d.price;
      state.suggestPrice = d.price;
      els.downsellAdd.textContent = "Added";
      els.add.disabled = true;
      els.add.classList.add("tpwce-is-added");
      els.addLabel.textContent = d.title.split(" ").pop() + " added";
      var s = els.add.querySelector("svg"); if (s) s.outerHTML = svgCheck();
      els.livePolite.textContent = d.title + " added to cart";
      analytics("ce_accepted", { rule: state.rule.id + ":downsell", suggestion: d.handle });
      // Founder: once added, close the whole sheet so they can move on.
      setTimeout(function () { closePanel(); }, 950);
    });
  }

  /* ============================================================
     ANALYTICS — fire our OWN telemetry only. We NEVER touch or
     swallow the native add_to_cart event (that stays product-form's).
     ============================================================ */
  function analytics(name, detail) {
    try { document.dispatchEvent(new CustomEvent("tpw:completion-engine:" + name, { detail: detail || {} })); } catch (e) {}
    try { if (window.dataLayer) window.dataLayer.push(Object.assign({ event: "tpw_" + name }, detail || {})); } catch (e) {}
  }

  /* ============================================================
     THE HOOK — additive, on the theme's own success event.
     variant:add carries detail.variant.id (the added variant id) and
     fires only AFTER product-form.js confirms the add succeeded. We do
     ONE /cart.js read to get the full line data (product_type/handle),
     then ONE /products/{handle}.js read to hydrate the added line's
     TAGS — cart line items carry no tags (the CE-03 root cause), and
     the tag-gated rules (R10 "HMD compatible", the exclusion
     kill-switch, tagNotAny guards) need them. Fail-open: if the
     product read fails, we resolve on cart fields alone.
     ============================================================ */
  var busy = false;
  var SESSION_KEY = "tpwceShown";
  function sessionFired() {
    try { return window.sessionStorage.getItem(SESSION_KEY) === "1"; } catch (e) { return false; }
  }
  function markSessionFired() {
    try { window.sessionStorage.setItem(SESSION_KEY, "1"); } catch (e) { /* private mode → per-page once */ }
  }
  document.addEventListener("variant:add", function (e) {
    if (busy) return;               // max ONE panel per add
    var vid = e && e.detail && e.detail.variant && e.detail.variant.id;
    if (!vid) return;
    busy = true;
    // small settle so the theme's own /cart.js + renderContents run first
    setTimeout(function () { runEngine(String(vid)); }, 120);
  });

  function hydrateTags(item, done) {
    var h = item && (item.handle || item.product_handle);
    if (!h || (item.tags && item.tags.length)) { done(); return; }
    fetch("/products/" + encodeURIComponent(h) + ".js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { if (p && p.tags) item.tags = p.tags; done(); })
      .catch(function () { done(); });
  }

  function runEngine(addedVariantId) {
    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        busy = false;
        if (!cart || !cart.items || !cart.items.length) return;
        // ONE card per session — survives page loads (CE-10)
        if (sessionFired()) return;
        // find the added line (match variant id; fall back to the last line)
        var addedItem = null;
        for (var i = 0; i < cart.items.length; i++) {
          if (String(cart.items[i].variant_id) === addedVariantId || String(cart.items[i].id) === addedVariantId) { addedItem = cart.items[i]; break; }
        }
        if (!addedItem) addedItem = cart.items[cart.items.length - 1];

        hydrateTags(addedItem, function () {
          var addedCat = resolveCategory(addedItem);
          if (!addedCat) return;                         // unknown / excluded → no-op
          if (addedCat === "EHMD" || addedCat === "PIPE_KIT" || addedCat === "BUNDLE_PREPKIT") return; // self-contained
          var picked = pick(addedCat, addedItem, cart.items);
          if (!picked) return;                           // nothing in stock to suggest → graceful no-op

          // don't suggest the very item just added
          if (picked.product && String(picked.product.variantId) === addedVariantId) return;

          // cart total BEFORE we add the suggestion (in cents)
          openWith(picked, addedItem, cart.total_price || 0);
        });
      })
      .catch(function () { busy = false; });
  }

})();
